import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { NAV_SECTIONS, SECTION_IDS } from "../../constants/sections.ts";
import { useScrollSpy } from "../../hooks/useScrollSpy.ts";
import { scrollToSection } from "../../utils/scrollToSection.ts";
import { cn } from "../../lib/utils.ts";

const HEADER_NAV = [
  { id: SECTION_IDS.comoFunciona, label: "Como Funciona" },
  { id: SECTION_IDS.sinais, label: "Sinais" },
  { id: SECTION_IDS.resultados, label: "Resultados" },
  { id: SECTION_IDS.precos, label: "Planos" },
  { id: SECTION_IDS.faq, label: "FAQ" },
];

export default function LandingHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { userEmail, logout } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const onLanding = location.pathname === "/";
  const sectionIds = useMemo(() => NAV_SECTIONS.map((item) => item.id), []);
  const activeSection = useScrollSpy(onLanding ? sectionIds : []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSectionNav = (sectionId: string) => {
    setMenuOpen(false);
    if (onLanding) {
      scrollToSection(sectionId);
      return;
    }
    navigate("/");
    window.setTimeout(() => scrollToSection(sectionId), 200);
  };

  const getInitial = () => userEmail?.charAt(0).toUpperCase() || "";

  return (
    <header
      className={cn(
        "landing-header-shell fixed inset-x-0 top-0 z-50 motion-safe:transition-all motion-safe:duration-200",
        scrolled && "landing-header-shell-scrolled"
      )}
    >
      <div className="relative mx-auto flex h-[72px] max-w-[1200px] items-center justify-between px-4 md:px-6">

        {/* Left: logo mark + wordmark + tagline */}
        <Link
          to="/"
          className="landing-header-brand flex shrink-0 items-center gap-2.5 rounded-full px-2 py-1.5"
          onClick={(e) => {
            if (!onLanding) return;
            e.preventDefault();
            scrollToSection(SECTION_IDS.inicio);
          }}
        >
          <img
            src="/BB-icon.png"
            alt=""
            className="h-10 w-auto object-contain"
          />
          <span className="hidden text-[16px] font-bold tracking-tight text-white sm:inline">
            Binary<span className="text-[#29d585]">Bucks</span>
          </span>
        </Link>

        {/* Center: nav pill — absolutely centered on desktop */}
        <nav className="landing-header-nav absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 items-center gap-1 rounded-full p-1.5 backdrop-blur-md lg:flex">
          {HEADER_NAV.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleSectionNav(id)}
              className={cn(
                "rounded-full px-4 py-2 text-[13px] font-bold motion-safe:transition-all motion-safe:duration-150",
                activeSection === id
                  ? "landing-header-nav-active text-[#021008]"
                  : "text-white/[0.78] hover:bg-white/[0.045] hover:text-white"
              )}
            >
              {label}
            </button>
          ))}
        </nav>

        {/* Right: auth + hamburger */}
        <div className="flex shrink-0 items-center gap-3">
          {userEmail ? (
            <div ref={dropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setShowMenu((prev) => !prev)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-tv-live text-xs font-bold text-tv-base"
                title={userEmail}
              >
                {getInitial()}
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full z-50 mt-2 min-w-[160px] overflow-hidden rounded-lg border border-tv-border bg-tv-surface py-1 shadow-panel">
                  {location.pathname !== "/dashboard" && (
                    <button
                      type="button"
                      className="block w-full px-4 py-2.5 text-left text-sm text-tv-text hover:bg-tv-elevated"
                      onClick={() => navigate("/dashboard")}
                    >
                      Dashboard
                    </button>
                  )}
                  <button
                    type="button"
                    className="block w-full px-4 py-2.5 text-left text-sm text-tv-text hover:bg-tv-elevated"
                    onClick={() => navigate("/profile")}
                  >
                    Perfil
                  </button>
                  <button
                    type="button"
                    className="block w-full px-4 py-2.5 text-left text-sm text-tv-sell hover:bg-tv-elevated"
                    onClick={logout}
                  >
                    Sair
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden rounded-full px-3 py-2 text-[13px] font-bold text-white/64 motion-safe:transition-colors motion-safe:duration-150 hover:bg-white/[0.045] hover:text-white lg:inline"
              >
                Entrar
              </Link>
              <Link
                to="/register"
                className="landing-header-cta ld-btn-primary ld-btn-sm ld-btn-pill"
              >
                Começar Agora
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </>
          )}

          <button
            type="button"
            className="ml-0.5 rounded-lg p-1.5 text-tv-muted motion-safe:transition-colors hover:bg-tv-surface hover:text-tv-text lg:hidden"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="border-t border-tv-border/40 bg-tv-base/95 px-4 pb-5 pt-3 backdrop-blur-md lg:hidden">
          <nav className="flex flex-col gap-0.5">
            {HEADER_NAV.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => handleSectionNav(id)}
                className={cn(
                  "rounded-lg px-3 py-2.5 text-left text-sm motion-safe:transition-colors",
                  activeSection === id
                    ? "bg-tv-live/10 font-medium text-tv-live"
                    : "text-tv-muted hover:bg-tv-surface/60 hover:text-tv-text"
                )}
              >
                {label}
              </button>
            ))}
          </nav>
          {!userEmail && (
            <div className="mt-3 flex flex-col gap-2 border-t border-tv-border/30 pt-3">
              <Link
                to="/login"
                className="block px-3 py-2 text-sm text-tv-muted"
                onClick={() => setMenuOpen(false)}
              >
                Entrar
              </Link>
              <Link
                to="/register"
                className="ld-btn-primary ld-btn-pill w-full"
                onClick={() => setMenuOpen(false)}
              >
                Começar Agora
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
